import React, { useEffect } from 'react';
import './BetNotification.css';

/**
 * Truncate error messages for notification display
 * Keep it short and readable in the toast
 */
function truncateNotificationMessage(message: string, maxLength: number = 80): string {
  if (message.length <= maxLength) {
    return message;
  }
  
  // Try to break at a natural point (sentence end, comma, etc.)
  const truncated = message.substring(0, maxLength);
  const lastPeriod = truncated.lastIndexOf('.');
  const lastComma = truncated.lastIndexOf(',');
  const lastSpace = truncated.lastIndexOf(' ');
  
  // Prefer breaking at period, then comma, then space
  const breakPoint = lastPeriod > 40 ? lastPeriod + 1 :
                     lastComma > 40 ? lastComma + 1 :
                     lastSpace > 40 ? lastSpace : maxLength;
  
  return message.substring(0, breakPoint).trim() + '...';
}

export interface BetNotification {
  id: string;
  type: 'bet-placed' | 'bet-won' | 'bet-error';
  amount?: number;
  asset?: string;
  multiplier?: number;
  payout?: number;
  errorMessage?: string; // For error notifications
}

interface BetNotificationProps {
  notification: BetNotification;
  onDismiss: (id: string) => void;
}

const BetNotificationToast: React.FC<BetNotificationProps> = ({ notification, onDismiss }) => {
  useEffect(() => {
    // Auto-dismiss after duration
    const duration = notification.type === 'bet-won' ? 4000 : 
                     notification.type === 'bet-error' ? 5000 : 
                     3000;
    const timer = setTimeout(() => {
      onDismiss(notification.id);
    }, duration);

    return () => clearTimeout(timer);
  }, [notification.id, notification.type, onDismiss]);

  const handleClick = () => {
    onDismiss(notification.id);
  };

  return (
    <div 
      className={`bet-notification ${notification.type}`}
      onClick={handleClick}
    >
      {notification.type === 'bet-placed' && (
        <>
          <div className="notification-header">
            <span className="notification-icon">💎</span>
            <span className="notification-title">BET PLACED</span>
          </div>
          <div className="notification-body">
            <img 
              src="/assets/icons/solanaLogoMark.svg" 
              alt="SOL" 
              className="notification-sol-icon"
            />
            {notification.amount} on {notification.asset} • {notification.multiplier?.toFixed(1)}x
          </div>
        </>
      )}

      {notification.type === 'bet-won' && (
        <>
          <div className="notification-header">
            <span className="notification-icon">🎉</span>
            <span className="notification-title">YOU WON!</span>
          </div>
          <div className="notification-body">
            <span className="notification-payout">+</span>
            <img 
              src="/assets/icons/solanaLogoMark.svg" 
              alt="SOL" 
              className="notification-sol-icon"
            />
            {notification.payout?.toFixed(3)} sent to wallet
          </div>
        </>
      )}

      {notification.type === 'bet-error' && (
        <>
          <div className="notification-header">
            <span className="notification-icon">⚠️</span>
            <span className="notification-title">BET FAILED</span>
          </div>
          <div className="notification-body notification-error-body">
            {truncateNotificationMessage(notification.errorMessage || 'An error occurred while placing your bet')}
          </div>
        </>
      )}
    </div>
  );
};

export default BetNotificationToast;

